import xhrService from '../xhrService';

const base = '/api/inspection-review';

export interface ReviewEligibleUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

/**
 * The raw review row, as returned by `GET /inspection-review/inspection/:inspectionId` — a
 * different (and more complete, e.g. `submitted_by_user_id`) shape than `CertStateReview`
 * (`utils/api/certification.ts`), which is the per-viewer summary `getState()` already returns
 * inline. Nothing on the certification page needs this shape today — `CertStateReview.id`/
 * `canSign`/`canCancel` are enough to drive `ReviewActions`/`RejectReviewModal` without a second
 * request — but the backend controller exposes it (mirroring `findingApproval.controller.ts`) for
 * a future reviewer-queue screen (SW2-2250 open question H), so the API surface is included here
 * for parity even though nothing calls `getByInspection`/`eligibleReviewers` yet.
 */
export interface InspectionReview {
  id: number;
  inspection_id: number;
  submitted_by_user_id: number;
  reviewer_role_id: number | null;
  reviewer_user_id: number | null;
  responded_by_user_id: number | null;
  resolved_via: 'manager' | 'user' | 'role' | null;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  rejection_reason: string | null;
  responded_at: string | null;
  created_at: string;
  updated_at: string;
}

const inspectionReviewApi = {
  getByInspection: (inspectionId: number): Promise<InspectionReview | null> =>
    xhrService
      .get(`${base}/inspection/${inspectionId}`)
      .then((r) => r.data ?? null),

  eligibleReviewers: (projectId: number): Promise<ReviewEligibleUser[]> =>
    xhrService
      .get(`${base}/eligible-reviewers`, { params: { projectId } })
      .then((r) => r.data),

  reject: (reviewId: number, reason: string): Promise<InspectionReview> =>
    xhrService
      .post(`${base}/${reviewId}/reject`, { reason })
      .then((r) => r.data),

  cancel: (reviewId: number): Promise<InspectionReview> =>
    xhrService.post(`${base}/${reviewId}/cancel`).then((r) => r.data),
};

export default inspectionReviewApi;
